import { EventEmitter } from '@angular/core';
import { sortIndexAscend, newUuid, StringUtils } from '@ark-libs/ngx-utils';

export enum TreeNodeActionType{
	HREF = 'href',
	SREF = 'sref'
}
export class TreeView {
	private static SEQ:string = "seq";

    uuid: string = newUuid();
    data: any;
    dataUrl: string;
    nonDataText: string; // 没有数据时显示的文本内容
    activeOnClick:boolean = false;
    enableCheckbox: boolean = false;
    dynamicChildrenLoad: boolean = true; // 是否动态加载子节点
	accordion:boolean = true;
	root: TreeNode;
    wholeTreeNodes: TreeNode[];

    loadChildrenHandler:Function = null;
    
    dataLoaded: EventEmitter<any> = new EventEmitter();
    childrenLoadError: EventEmitter<TreeNode> = new EventEmitter();
    childrenLoaded: EventEmitter<TreeNode> = new EventEmitter();
	checkedChange: EventEmitter<TreeNode> = new EventEmitter();
	openChange: EventEmitter<TreeNode> = new EventEmitter();
	selectChange: EventEmitter<TreeNode> = new EventEmitter();

    constructor(options?: { data?: any }) { 
    	this.patchOptions(options);
    	this.openChange.subscribe(treeNode=>this.onOpenChange(treeNode));
        this.selectChange.subscribe(treeNode=>this.onSelectChange(treeNode));
        this.childrenLoaded.subscribe(treeNode=>{treeNode.childrenLoading=false;});
    }

    private patchOptions(options?: any){
        if(!options) this.data = {};
    	else if(!options.data) this.data = {children:[]};
		else if(Array.isArray(options.data))this.data={children:[...options.data]};
		else this.data = options.data;

		this.wholeTreeNodes = [];
        this.root = new TreeNode(this, {data:this.data});
        this.root.opened = true;
    }

	public get items(): TreeNode[]{
		return this.root?this.root.children:[];
    }
    
    public get checkedNodes(){
        return this.wholeTreeNodes.filter(item=>item.checked);
    }
    public getSelectedNode(){
        return this.wholeTreeNodes.filter(item=>item.selected).shift();
    }
    public loadData(data?:any){
        if(data == null){
            this.loadChildren();
        }else{
            this.root.loadData(data);
        }
    }
    public loadChildren(treeNode?:TreeNode){
        if(treeNode == null) treeNode = this.root;

        if(this.loadChildrenHandler == null||treeNode.childrenLoading) return;
        treeNode.childrenLoading = true; 
        
        if(treeNode.childrenLoadingTimer){
            clearTimeout(treeNode.childrenLoadingTimer);
            treeNode.childrenLoadingTimer = null;
        }
        treeNode.childrenLoadingTimer = setTimeout(() => { // 延迟调用
            treeNode.childrenLoadingTimer = null;
            try{
                // console.log("loadChildren ...", treeNode);
                this.loadChildrenHandler(treeNode);
            }catch(e){
                treeNode.childrenLoading = false; 
                console.error("TreeView load children error.", e);
            }
        }, 200);
    }

    private autoLoadChildren(treeNode: TreeNode){
        if(this.dynamicChildrenLoad && !treeNode.childrenLoaded){
            this.loadChildren(treeNode);
        }
    }

    private onOpenChange(treeNode: TreeNode){
    	if(treeNode&&treeNode.opened){
            this.openTo(treeNode);
            this.autoLoadChildren(treeNode);
        }
    }

    private onSelectChange(treeNode: TreeNode){
    	if(treeNode&&treeNode.selected){
    		this.wholeTreeNodes
    			.filter(item=>item.selected&&item.uuid!=treeNode.uuid)
                .forEach(item=> {item.selected = false;});
            this.autoLoadChildren(treeNode);
    	}
    }

    public select(treeNode?: TreeNode, event?:any){
        if(treeNode==null){
            this.wholeTreeNodes.forEach(item=>item.selected=false);
        }else if(this.activeOnClick||treeNode.action){
	    	treeNode.selected = true;
	    }
    }

    public openToSelected(){
    	this.openTo(this.wholeTreeNodes.filter(item=>item.selected).shift(),true);
    }

    public openTo(treeNode: TreeNode, force?:boolean){
		let ancestors: TreeNode[] = [];
		let parent = treeNode;
		while(parent){
			if(force) parent.opened = true;
			ancestors.push(parent);
			parent = parent.parent;
		}
        if(this.accordion){
    		this.wholeTreeNodes.filter(item=>item.opened).forEach(item=>{
    			if(!ancestors.some(ancestor=>ancestor.uuid === item.uuid)){
    				item.opened = false;
    			}
    		});
        }
    }

    public getNode(uuid:string): TreeNode{
        return this.wholeTreeNodes.find(item=>item.uuid==uuid);
    }
    public findNodes(params:any):TreeNode[]{
        return this.wholeTreeNodes.filter(treeNode=>{
            Object.keys(params).forEach(p => {
                if(params[p] != treeNode[p]) return false;
            })
            return true;
        });
    }
    public findNodeByUrl(url:string): TreeNode{
        return this.wholeTreeNodes.find(item=>item.isMatchedUrl(url));
    }
    public pathFromRootByUrl(url: string): TreeNode[]{
        let treeNode;
        while(!treeNode){
            treeNode = this.findNodeByUrl(url);
            if(treeNode||url.lastIndexOf("/")<=0||url.indexOf("http")==0) break;
            url = url.substring(0, url.lastIndexOf("/"));
        }

        if(treeNode == null) return [];
        let parent = treeNode;
        let paths = [];
        const maxLevel = 20;
        while(parent){
			if(parent == this.root) break;
            paths.unshift(parent);
            if(paths.length>maxLevel) break;
            parent = parent.parent;
        }
        return paths;
    }
}
export class TreeNode {
	data: any;

	uuid: string = newUuid();
	parentUuid: string;
	treeView: TreeView;
	parent: TreeNode;
    children: TreeNode[];
    draggable: { mode?:'move'|'copy',
        enableDrop?:boolean, dropZone?: string, drop?:EventEmitter<any>,
        enableDrag?:boolean, targetDropZones?:string[], drag?: EventEmitter<any> 
    }={};

	seq: number;
    private _title: string;
    private _titleAdditional: string;
	icon: string;
	actionType: TreeNodeActionType;
	action: string;
    visible: boolean;

    childrenLoadingTimer = null;
    childrenLoading: boolean = false;
    childrenLoaded: boolean = true;
    private _childrenLoadError: any = null;
    
    checkedIndeterminate: boolean = false;

    private _enableCheckbox;
	private _checked: boolean;
	private _opened: boolean;
	private _selected: boolean;
    private _childrenUrl: string;

    constructor(treeView:TreeView, options?: { parent?:TreeNode, data?: any }) { 
    	this.treeView = treeView;
    	this.patchOptions(options);
    	this.treeView.wholeTreeNodes.push(this);
    }

    private patchOptions(options?: any, keepState?:boolean){
        if(!options) options = {};
        if(this.parent == null) this.parent = options.parent;

        const data = Array.isArray(options.data)?{children:options.data}:(options.data||{});
    	this.parentUuid = this.parent?this.parent.uuid:null;
    	this.seq = data.seq||data.index;
        this._title = data.title||data.label||data.text||data.name;
        this._titleAdditional = data.titleAdditional;
    	this.icon = data.icon||'anticon anticon-tag-o';
        this.visible = data.invisible!=null?!data.invisible:(data.visible==null?true:!!data.visible);

		this.actionType = StringUtils.isEmpty(data.action)?null: (data.actionType?data.actionType.toLowerCase():TreeNodeActionType.SREF);
    	this.action = data.action?data.action.replace(/\$*\{*localhost\}*|\$*\{host\}/ig, self.location.hostname):null;

        this.checkedIndeterminate = !!data.checkedIndeterminate;
        this._checked = !!data.checked;
        this._enableCheckbox = data.enableCheckbox;
        this._childrenUrl = data.childrenUrl;
    	this._opened = !!data.opened;
    	this._selected = !!data.selected;

        if(data.draggable) this.draggable = data.draggable;
        else if(options.draggable) this.draggable = options.draggable;
        
    	this.data = data;
        this.replaceChildrenData(data);
        if(this.treeView.dynamicChildrenLoad && this._childrenUrl && this.children.length==0){
            this.childrenLoaded = false;
        }
    }

    private replaceChildrenData(data: any){
        this.childrenLoading = false;
        this.childrenLoaded = true;

        if(this.children != null){
            // 将当前节点从whole中移除
            const wholeTreeNodes = this.treeView.wholeTreeNodes;
            this.children.forEach(child=>{
                child.removeInWhileTreeNodes();
            });
        }
        this.children = [];
        if(data == null) return;
        // 生成子节点
        let children = Array.isArray(data)?data:(data.children||data.items||[]);
    	children.sort((a,b)=>sortIndexAscend(a,b)).forEach(child=>{
    		this.children.push(new TreeNode(this.treeView, {parent: this, data:child}));
        });
    }
    // 解晰参数引用
    private resolveParamRef(text:string, selfProperties?:string) {
        if (text == null || text.indexOf('${') == -1) return text;
        if(selfProperties) text = text.replace('${' + selfProperties + '}', '');
        Object.keys(this).some(p => {
            text = text.replace('${' + p + '}', this[p]);
            return (text.indexOf('${') == -1);
        })
        Object.keys(this.data).some(p => {
            text = text.replace('${data.' + p + '}', this.data[p]);
            return (text.indexOf('${') == -1);
        })
        return text;
    }

    public get isRoot():boolean{
        return this == this.treeView.root;
    }

    
    public get title() : string {
        return this.resolveParamRef(this._title,'title');
    }
    public set title(v : string) {
        this._title = v;
    }
    
    public get titleAdditional() : string {
        return this.resolveParamRef(this._titleAdditional,'titleAdditional');
    }
    public set titleAdditional(v : string) {
        this._titleAdditional = v;
    }
    
    
    public get enableCheckbox():boolean{
        return this._enableCheckbox==null?this.treeView.enableCheckbox:!!this._enableCheckbox;
    }
    public set enableCheckbox(enable: boolean){
        this._enableCheckbox = enable;
    }

    public get hasChildren() : boolean {
        return !this.childrenLoaded||this.children.length>0;
    }
    public get childrenUrl(): string {
        let patten = this._childrenUrl;
        if(this == this.treeView.root) patten = this.treeView.dataUrl;
        return this.resolveParamRef(patten, 'childrenUrl');
    }
    public set childrenUrl(url: string){
        if(this._childrenUrl == url) return;
        this.childrenLoaded = (this.children&&this.children.length>0);
        this._childrenUrl = url;
    }

    public get checked() : boolean {
    	return this._checked;
    }
    public set checked(v : boolean) {
    	this.setChecked(v);
    }
    public setChecked(v:boolean, emitCheckedChange:boolean=true){
    	if(this._checked != v){
	    	this._checked = v;
	    	if(emitCheckedChange) this.treeView.checkedChange.emit(this);
	    }
    }

    public get opened() : boolean {
    	return this._opened;
    }
    public set opened(v : boolean) {
        this.setOpened(v);
    }
    public setOpened(v : boolean, emitOpenChange:boolean=true) {
    	if(this._opened != v){
	    	this._opened = v;
	    	if(emitOpenChange) this.treeView.openChange.emit(this);
	    }
    }

    public get selected() : boolean {
    	return this._selected;
    }
    public set selected(v : boolean) {
        this.setSelected(v);
    }
    public setSelected(v : boolean, emitSelectChange:boolean=true) {
    	if(this._selected != v){
	    	this._selected = v;
	    	if(emitSelectChange) this.treeView.selectChange.emit(this);
	    }
    }

    public isMatchedUrl(url:string): boolean{
        if(url==null||url==undefined||url.length==0) return false;

        let moreChars = (this.action&&this.action.length>url.length)?this.action.substring(url.length-1):"";
        let isMatch = this.action!=null&&this.action.indexOf(url)==0&&(moreChars.length==0||moreChars.indexOf("?")==0||moreChars.indexOf("#")==0);
        // console.log("action="+this.action, "url="+url, "isMatch="+isMatch);
        return isMatch;
    }


    get childrenLoadError():any{
        return this._childrenLoadError;
    }
    set childrenLoadError(error:any){
        this.childrenLoading = false;
        this._childrenLoadError = error;
        this.treeView.childrenLoadError.emit(this);
    }
    public loadChildren(data?: any){
        if(data == null){
            this.treeView.loadChildren(this);
        }else{
            this.childrenLoadError = null;
            this.treeView.dataLoaded.emit(data);
            this.replaceChildrenData(data);
            this.treeView.childrenLoaded.emit(this);
        }
    }
    public loadData(nodeData:any){
        this.patchOptions({data:nodeData});
    }
    public removeNode(){
        if(this.parent==null||this == this.treeView.root){ // root节点不能被删除
            // 从父节点中移除
            const indexOfChildren = this.parent.children.indexOf(this);
            if(indexOfChildren != -1) this.parent.children.splice(indexOfChildren);

            // 递归删除保存在WhileTreeNodes中的子孙节点
            this.removeInWhileTreeNodes();
        }
    }
    public removeAllChildren(){
        this.children.forEach(child=>{
            child.removeNode();
        });
    }
    private removeInWhileTreeNodes(){
        const wholeTreeNodes = this.treeView.wholeTreeNodes;
        const indexOfWhole = wholeTreeNodes.indexOf(this);
        if(indexOfWhole != -1) wholeTreeNodes.splice(indexOfWhole);

        // 递归删除子孙节点
        this.children.forEach(child=>{
            child.removeInWhileTreeNodes();
        });
    }
}